/**
 * ExecutiveDashboard — Phase 1.7g
 * Four-panel auto-collected, class-level metrics dashboard:
 *   1. Adoption Velocity     — weekly AI Tutor usage line
 *   2. Trust Badge Distribution — donut of citation reliability
 *   3. Topic Confusion       — horizontal bar of question-heavy topics
 *   4. Outcomes Correlation  — scatter of chat usage vs composite score
 *
 * Auto-refreshes every 30s. Module dropdown lets faculty drill in;
 * default "All modules" is the Dean / executive view.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  BarChart, Bar, LabelList,
  ScatterChart, Scatter, ZAxis, ReferenceLine,
} from 'recharts';
import Navbar from '../components/Navbar';
import ConsoleDrawer from '../components/ConsoleDrawer';
import { getExecutiveDashboard } from '../lib/apiClient';
import { getExecutiveRecallSummary } from '../lib/studyApi';

const COLORS = {
  high:   '#1f8a4c',
  medium: '#f5b400',
  low:    '#d93b3b',
  primary: '#00853E',
  accent:  '#1f6feb',
};

const Panel = ({ title, subtitle, children, testid, action }) => (
  <section className="card p-5" data-testid={testid}>
    <div className="flex items-start justify-between mb-3 gap-2">
      <div>
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const fmtRelative = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  return `${Math.round(sec / 3600)}h ago`;
};

const ExecutiveDashboard = () => {
  const [moduleId, setModuleId] = useState('');
  const [weeks, setWeeks] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [tick, setTick] = useState(0);
  const [recall, setRecall] = useState(null);
  const intervalRef = useRef(null);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    setErr('');
    const params = { weeks };
    if (moduleId) params.module_id = moduleId;
    return getExecutiveDashboard(params)
      .then(setData)
      .catch((e) => setErr(e.response?.data?.detail || 'Failed to load'))
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [moduleId, weeks]);

  useEffect(() => {
    const fetchRecall = () => getExecutiveRecallSummary()
      .then(setRecall).catch(() => {});
    fetchRecall();
    const t = setInterval(fetchRecall, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!autoRefresh) return undefined;
    intervalRef.current = setInterval(() => {
      load(true);
      setTick((t) => t + 1);
    }, 30000);
    return () => intervalRef.current && clearInterval(intervalRef.current);
    /* eslint-disable-next-line */
  }, [autoRefresh, moduleId, weeks]);

  if (loading && !data) return (
    <div className="min-h-screen"><Navbar /><div className="p-8 text-gray-500">Loading executive dashboard…</div></div>
  );

  // ─── Panel data shaping ────────────────────────────────────────────
  const adoption = (data?.adoption_velocity?.points || []).map((p) => ({
    label: p.label, usage_pct: p.usage_pct, active: p.active_students,
  }));

  const trust = data?.trust_distribution || { high: { count: 0, pct: 0 }, medium: { count: 0, pct: 0 }, low: { count: 0, pct: 0 }, total_citations: 0 };
  const trustData = [
    { name: 'High Confidence (≥80%)', key: 'high', value: trust.high.count, pct: trust.high.pct, color: COLORS.high },
    { name: 'Medium Confidence (50–79%)', key: 'medium', value: trust.medium.count, pct: trust.medium.pct, color: COLORS.medium },
    { name: 'Low Confidence / Hallucination (<50%)', key: 'low', value: trust.low.count, pct: trust.low.pct, color: COLORS.low },
  ];

  const topics = data?.topic_confusion?.topics || [];
  const threshold = data?.topic_confusion?.threshold || 10;
  // Recharts uses array order; we want longest bar at top.
  const topicsForChart = [...topics].sort((a, b) => a.question_count - b.question_count).map((t) => ({
    name: t.topic_title,
    count: t.question_count,
    hot: t.is_hot,
  }));

  const outcomes = data?.outcomes_correlation?.points || [];
  const pearson = data?.outcomes_correlation?.pearson_r;

  return (
    <div className="min-h-screen" data-testid="executive-dashboard">
      <Navbar />
      <ConsoleDrawer role="executive">
      <main className="max-w-7xl mx-auto px-6 py-8 pl-16">
        {/* Header — green band like the mock */}
        <div className="rounded-t-lg px-5 py-3 mb-0 flex items-center justify-between flex-wrap gap-3"
             style={{ background: COLORS.primary, color: 'white' }}>
          <div>
            <div className="text-[11px] uppercase tracking-wider opacity-80">Class-level metrics · auto-collected · real-time</div>
            <h1 className="text-xl font-bold">Executive Dashboard</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={moduleId}
              onChange={(e) => setModuleId(e.target.value)}
              className="input-field text-sm py-1"
              style={{ color: '#111' }}
              data-testid="exec-module-filter"
            >
              <option value="">All modules</option>
              {(data?.modules || []).map((m) => (
                <option key={m.id} value={m.id}>Module {m.order}: {m.title}</option>
              ))}
            </select>
            <select
              value={weeks}
              onChange={(e) => setWeeks(Number(e.target.value))}
              className="input-field text-sm py-1"
              style={{ color: '#111' }}
              data-testid="exec-weeks-filter"
            >
              <option value={4}>Last 4 weeks</option>
              <option value={8}>Last 8 weeks</option>
              <option value={14}>Last 14 weeks</option>
              <option value={26}>Last 26 weeks</option>
            </select>
            <label className="text-xs flex items-center gap-1.5">
              <input type="checkbox" checked={autoRefresh}
                     onChange={(e) => setAutoRefresh(e.target.checked)}
                     data-testid="exec-autorefresh" />
              Auto-refresh
            </label>
            <button
              onClick={() => load(true)}
              className="text-xs px-2 py-1 rounded bg-white/15 hover:bg-white/25"
              data-testid="exec-refresh-btn"
              title="Refresh now"
            >
              ↻
            </button>
          </div>
        </div>

        {err && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}

        <div className="rounded-b-lg border border-t-0 border-gray-200 bg-white p-4 mb-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 1. Adoption Velocity */}
          <Panel
            title="Adoption Velocity"
            subtitle="Weekly AI Tutor usage (% of distinct students active)"
            testid="panel-adoption"
          >
            <div style={{ width: '100%', height: 260, minHeight: 260 }}>
              <ResponsiveContainer>
                <LineChart data={adoption} margin={{ top: 14, right: 18, left: -8, bottom: 0 }}>
                  <CartesianGrid stroke="#eee" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Line type="monotone" dataKey="usage_pct" stroke={COLORS.high} strokeWidth={2.5}
                        dot={{ r: 3, fill: COLORS.high }} activeDot={{ r: 5 }}>
                    <LabelList dataKey="usage_pct" position="top" fontSize={10}
                               formatter={(v) => `${v}%`} />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Current: <strong className="text-gray-900">{data?.adoption_velocity?.current_pct?.toFixed?.(1) || 0}%</strong> ·
              {' '}{data?.adoption_velocity?.total_students_window || 0} distinct students seen in window
            </div>
          </Panel>

          {/* 2. Trust Distribution */}
          <Panel
            title="Trust Badge Distribution"
            subtitle="AI Citation Reliability Breakdown"
            testid="panel-trust"
          >
            <div style={{ width: '100%', height: 260, minHeight: 260 }}>
              {trust.total_citations === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  No citations recorded yet in this window.
                </div>
              ) : (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={trustData} dataKey="value" cx="40%" cy="50%"
                         innerRadius={55} outerRadius={85} paddingAngle={2}
                         label={({ percent }) => percent > 0.04 ? `${Math.round(percent * 100)}%` : ''}
                         labelLine={false}>
                      {trustData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Legend layout="vertical" align="right" verticalAlign="middle"
                            iconType="square"
                            formatter={(_, entry) => {
                              const d = trustData.find((x) => x.name === entry.value);
                              return (
                                <span style={{ color: '#333', fontSize: 11 }}>
                                  {entry.value} <strong>{d?.value || 0}</strong>
                                </span>
                              );
                            }} />
                    <Tooltip formatter={(v, n) => [`${v} citations`, n]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-2 text-center">
              <strong className="text-gray-900">{trust.total_citations}</strong> total citations recorded
            </div>
          </Panel>

          {/* 3. Topic Confusion */}
          <Panel
            title="Topic Confusion Signal"
            subtitle={`Topics generating student questions (hot ≥ ${threshold})`}
            testid="panel-topics"
          >
            <div style={{ width: '100%', height: 260, minHeight: 260 }}>
              {topicsForChart.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  No topic-scoped questions yet.
                </div>
              ) : (
                <ResponsiveContainer>
                  <BarChart data={topicsForChart} layout="vertical" margin={{ top: 8, right: 30, left: 4, bottom: 8 }}>
                    <CartesianGrid stroke="#eee" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                    <Tooltip formatter={(v) => [`${v} questions`, 'count']} />
                    <ReferenceLine x={threshold} stroke="#d93b3b" strokeDasharray="3 3" />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {topicsForChart.map((t, i) => (
                        <Cell key={i} fill={t.hot ? COLORS.medium : '#c8d0d8'} />
                      ))}
                      <LabelList dataKey="count" position="right" fontSize={11} fill="#444" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Panel>

          {/* 4. Outcomes Correlation */}
          <Panel
            title="Outcomes Correlation"
            subtitle={`Chat usage vs assignment scores (Pearson r${pearson != null ? ` = ${pearson}` : ': n/a'})`}
            testid="panel-outcomes"
          >
            <div style={{ width: '100%', height: 260, minHeight: 260 }}>
              {outcomes.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  Need at least one student with a chat history + finalized graded work.
                </div>
              ) : (
                <ResponsiveContainer>
                  <ScatterChart margin={{ top: 8, right: 14, left: -8, bottom: 12 }}>
                    <CartesianGrid stroke="#eee" />
                    <XAxis type="number" dataKey="chat_count" name="Chat Usage Frequency"
                           tick={{ fontSize: 11 }}
                           label={{ value: 'Chat Usage Frequency', position: 'bottom', offset: -2, style: { fontSize: 11, fill: '#555' } }} />
                    <YAxis type="number" dataKey="composite_pct" name="Composite %"
                           domain={[0, 100]} tick={{ fontSize: 11 }}
                           label={{ value: 'Composite %', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#555' } }} />
                    <ZAxis range={[55, 55]} />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }}
                             formatter={(v, n) => [n === 'Composite %' ? `${v}%` : v, n]}
                             labelFormatter={() => ''} />
                    <Scatter data={outcomes} fill={COLORS.high} fillOpacity={0.7} />
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-2 text-center">
              <strong className="text-gray-900">{outcomes.length}</strong> student{outcomes.length === 1 ? '' : 's'} with both chat history & finalized grades
            </div>
          </Panel>
        </div>

        {recall && (
          <div className="rounded-lg border border-gray-200 bg-white p-5 mt-4"
               data-testid="exec-recall-panel">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">
                  Active Recall · Institutional roll-up
                </div>
                <h2 className="text-lg font-bold text-gray-900 mt-0.5">
                  🧠 Recall/Connect Adoption & Health
                </h2>
                <div className="text-xs text-gray-500 mt-1">
                  Aggregated from every card review, Feynman grade, and peer duel across the tenant.
                </div>
              </div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/insights/executive/export.csv`}
                 target="_blank"
                 rel="noreferrer"
                 className="text-xs px-3 py-1.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                 data-testid="exec-recall-csv-link">
                Export CSV
              </a>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[
                ['Weekly Active Learners', recall.counts.weekly_active_learners, `of ${recall.counts.total_users} users`],
                ['Total Reviews', recall.counts.total_reviews, `${recall.counts.total_decks} decks · ${recall.counts.total_cards} cards`],
                ['Avg Recall Mastery', `${recall.quality.avg_recall_mastery}%`, `blend of accuracy × freshness`],
                ['Peer Duels Complete', recall.counts.peer_duels_complete,
                 `${recall.counts.peer_duels_active} active · ${recall.counts.peer_invites_pending} pending`],
              ].map(([label, val, sub]) => (
                <div key={label} className="rounded p-3 bg-gray-50 border border-gray-200"
                     data-testid={`exec-recall-kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                    {label}
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-0.5">{val}</div>
                  {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold mb-2">
                  Card-strategy distribution (5-recall stack)
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(recall.quality.card_type_distribution || {}).map(([t, n]) => (
                    <span key={t} data-testid={`exec-recall-type-${t}`}
                          className="text-xs px-2 py-1 rounded bg-emerald-50 border border-emerald-200">
                      <b className="text-emerald-800">{t}</b>: {n}
                    </span>
                  ))}
                </div>
                <div className="text-[11px] text-gray-500 mt-2">
                  Every deck contains all 5 strategies (standard, confused, bridge, cloze, feynman).
                  A collapse here signals content-generation drift.
                </div>
              </div>
              <div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold mb-2">
                      Top learners
                    </div>
                    {recall.leaderboard.top_5.length === 0 ? (
                      <div className="text-xs text-gray-500">No data yet.</div>
                    ) : recall.leaderboard.top_5.map((s) => (
                      <div key={s.user_email} className="text-xs flex justify-between py-1 border-b border-gray-100"
                           data-testid={`exec-top-${s.user_email}`}>
                        <span className="truncate">{s.user_email}</span>
                        <b className="text-emerald-700">{s.recall_mastery}%</b>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-red-700 font-bold mb-2">
                      Need support
                    </div>
                    {recall.leaderboard.bottom_5.length === 0 ? (
                      <div className="text-xs text-gray-500">No data yet.</div>
                    ) : recall.leaderboard.bottom_5.map((s) => (
                      <div key={s.user_email} className="text-xs flex justify-between py-1 border-b border-gray-100"
                           data-testid={`exec-bottom-${s.user_email}`}>
                        <span className="truncate">{s.user_email}</span>
                        <b className="text-red-700">{s.recall_mastery}%</b>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-2 text-xs text-gray-500 flex-wrap gap-2">
          <span>Faculty don't need to build a research apparatus — all four metrics are auto-collected.</span>
          <span>
            Generated <strong>{fmtRelative(data?.generated_at)}</strong>
            {autoRefresh && <> · auto-refresh every 30s · tick {tick}</>}
            {' '}·
            {' '}<Link to="/chat-efficiency" className="text-emerald-700 underline" data-testid="exec-chat-eff-link">Drill into Chat Efficiency</Link>
            {' '}·
            {' '}<Link to="/gradebook" className="text-emerald-700 underline" data-testid="exec-gradebook-link">Open Gradebook</Link>
          </span>
        </div>
      </main>
      </ConsoleDrawer>
    </div>
  );
};

export default ExecutiveDashboard;
